import {
  useKineticMap,
  useStableResultData,
  getNodes,
  MapContext,
  MapSearchResultItem,
  DEFAULT_ZOOM,
  useMapBoxSearchContext,
  useMapSearchBoxPin,
  MapSearchResultProps,
  publicUrl,
  AppSearchItemType,
  AppSearchIconKey,
  useAppSearchHistory,
} from '@kinetic-ui/shared';
import { useWeatherStationsListQuery } from '@kinetic-ui/data-layer';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import CloudIcon from '@mui/icons-material/Cloud';
import { ENTITY_DRAWER_VIEW_ALL_ROUTE, ENTITY_EDIT_ROUTE } from '../EntityList/consts';
import { WeatherLayerState } from '../layers/Weather/types';

const MapSearchResult: React.FC<MapSearchResultProps> = ({ isAppSearch, onResultClick }) => {
  const map = useKineticMap();
  const { searchText } = useMapBoxSearchContext();
  const result = useWeatherStationsListQuery({
    fetchPolicy: 'cache-and-network',
    variables: { searchText, limit: 5 },
  });
  const resultData = useStableResultData(result);
  const { selectedLayers, setSelectedLayers, hasEntityDrawer } = useContext(MapContext);
  const { addMapPin, removeMapPin } = useMapSearchBoxPin();
  const { addSearchHistory } = useAppSearchHistory();

  const weatherStations = useMemo(
    () => getNodes(resultData?.weatherStations),
    [resultData?.weatherStations]
  );
  const { push } = useHistory();

  const onClick = useCallback(
    (id: string, location?: any, openInNewWindow?: boolean) => {
      if (isAppSearch) {
        const path = `/${ENTITY_EDIT_ROUTE}/${id}/`;
        if (openInNewWindow) {
          window.open(publicUrl(path), '_blank');
        } else {
          push(path);
        }
        const station = weatherStations.find((e) => e.id === id);
        if (station?.name) {
          addSearchHistory({
            name: station.name,
            link: path,
            type: AppSearchItemType.View,
            icon: AppSearchIconKey.Cloud,
          });
        }
        if (onResultClick) {
          onResultClick();
        }
        return;
      }

      if (!hasEntityDrawer) {
        addMapPin({ type: 'Point', coordinates: location?.coordinates ?? [0, 0] });
        return;
      }
      !selectedLayers.includes(WeatherLayerState.WEATHER_STATIONS) &&
        setSelectedLayers([...selectedLayers, WeatherLayerState.WEATHER_STATIONS]);
      push(`/${ENTITY_DRAWER_VIEW_ALL_ROUTE}/${id}`);
      map.setViewState({
        latitude: location?.coordinates?.[1] ?? 0,
        longitude: location?.coordinates?.[0] ?? 0,
        zoom: DEFAULT_ZOOM,
      });
    },
    [
      addMapPin,
      addSearchHistory,
      hasEntityDrawer,
      isAppSearch,
      map,
      onResultClick,
      push,
      selectedLayers,
      setSelectedLayers,
      weatherStations,
    ]
  );

  useEffect(() => {
    !hasEntityDrawer && searchText?.length > 0 && removeMapPin();
    // only do this when searchText changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      {weatherStations.map((weatherStation) => (
        <MapSearchResultItem
          key={`searchItem_${weatherStation.id}`}
          name={weatherStation.name}
          onClick={(openInNewWindow) =>
            onClick(weatherStation.id, weatherStation.location, openInNewWindow)
          }
          icon={<CloudIcon />}
          newWindow={isAppSearch}
        />
      ))}
    </>
  );
};

export default MapSearchResult;
